import { Assets, Container, Graphics } from 'pixi.js';
import type { Texture } from 'pixi.js';
import type { CarSlot, Scores, ServerMessage } from '@nanocars/shared';
import { LOGICAL_WIDTH, type Scene, type SceneManager } from './SceneManager';
import { UI_PATHS } from '../assets/manifest';
import { COLORS, colourForSlot, lighten } from '../ui/theme';
import { makeImageButton, makeLabel } from '../ui/widgets';
import { FocusRing, type FocusItem } from '../ui/focus';
import { session } from '../state/Session';
import { TitleScene } from './TitleScene';
import { LobbyScene } from './LobbyScene';

type ResultsMessage = Extract<ServerMessage, { t: 'results' }>;

export interface ResultsRow {
  readonly rank: number;
  readonly carId: number;
  readonly name: string;
  readonly colour: number;
  readonly points: number;
  readonly isWinner: boolean;
}

// Pure (no Pixi/DOM) so a plain unit test can cover row order and the
// winner flag directly: builds display rows in the SERVER's `ranking`
// order. The server has already resolved any point ties when it computed
// `ranking` (see shared sim/race.ts rankCars), so this never re-sorts by
// score - two cars tied on points must still render in ranking order, not
// swap places based on `scores` alone.
export function buildResultsRows(
  ranking: readonly number[],
  scores: Scores,
  slots: readonly CarSlot[],
): ResultsRow[] {
  return ranking.map((carId, i) => {
    const slot = slots.find((s) => s.id === carId);
    return {
      rank: i + 1,
      carId,
      name: slot?.name ?? `Car ${carId}`,
      colour: colourForSlot(slot?.colour ?? -1),
      points: scores[carId] ?? 0,
      isWinner: i === 0,
    };
  });
}

const PANEL_WIDTH = 640;
const PANEL_X = (LOGICAL_WIDTH - PANEL_WIDTH) / 2;
const PANEL_TOP = 130;
const PANEL_PAD_Y = 22;
const ROW_HEIGHT = 58;
const BUTTON_WIDTH = 220;
const BUTTON_HEIGHT = 80;
const BUTTON_GAP = 20;
const CONTROLS_GAP_Y = 44;

// Results: a ranked list of every car in the just-finished race (rank,
// colour swatch, name, points, a "Winner" badge on rank 1), a host-only
// Rematch button (everyone else sees "Waiting for host..."), and Leave.
// Rematch/leave/lobby transitions mirror LobbyScene's pattern of sending
// the intent and letting the server's next message drive the actual scene
// change (here, `lobby` - the server flips phase back to 'lobby' and
// broadcasts it to everyone, host included, on a successful rematch).
export class ResultsScene implements Scene {
  readonly name = 'results';
  readonly container = new Container();
  private readonly content = new Container();

  private buttonTexture: Texture | null = null;
  private focusRing: FocusRing | null = null;
  private readonly unsubscribers: Array<() => void> = [];

  constructor(
    private readonly scenes: SceneManager,
    private readonly results: ResultsMessage,
  ) {}

  async enter(): Promise<void> {
    this.container.addChild(this.content);
    this.buttonTexture = await Assets.load(UI_PATHS.button);
    this.focusRing = new FocusRing(this.container, session.input);

    this.unsubscribers.push(
      // The server also sends a `lobby` broadcast right after `results`
      // itself (see server/room.ts finishRace(), which calls
      // broadcastLobby() purely for LobbyState bookkeeping) - that one
      // carries `lobby.phase === 'results'`, not `'lobby'`. Only a real
      // rematch (handleRematch sets phase='lobby' before broadcasting)
      // produces `lobby.phase === 'lobby'`; treating every `lobby` message
      // as "go to LobbyScene" bounced straight off this scene the instant
      // it was entered.
      session.net.on('lobby', (msg) => {
        // Every lobby message updates Session, unconditionally: this scene
        // reads session.lobby to decide whether to show the host-only
        // Rematch button, so a host migration while the results are up (the
        // host closed the tab) must be reflected here rather than leaving a
        // stale "Waiting for host..." on the new host's screen. Only the
        // SCENE CHANGE is gated on phase.
        session.lobby = msg.lobby;
        if (msg.lobby.phase === 'lobby') {
          void this.scenes.go(new LobbyScene(this.scenes));
          return;
        }
        this.render();
      }),
    );

    this.render();
  }

  exit(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers.length = 0;
    this.focusRing?.dispose();
    this.content.removeChildren();
  }

  update(_dt: number): void {
    // Static ranking list; nothing to animate.
  }

  resize(_w: number, _h: number): void {
    // Fixed 1280x720 logical layout.
  }

  private render(): void {
    // Destroy, not just detach: render() re-runs on every `lobby` message
    // (host migration while the results are up), and plain removeChildren
    // would leak the previous pass's Graphics/Text GPU resources. `texture`
    // is left alone so the shared, Assets-cached button texture survives.
    for (const child of this.content.removeChildren()) child.destroy({ children: true });
    const focusItems: FocusItem[] = [];

    const title = makeLabel('Race results', { fontSize: 34, fontWeight: '900', fill: COLORS.textLight });
    title.anchor.set(0.5, 0);
    title.x = LOGICAL_WIDTH / 2;
    title.y = 44;
    this.content.addChild(title);

    const rows = buildResultsRows(this.results.ranking, this.results.scores, this.results.slots);
    const panelHeight = PANEL_PAD_Y * 2 + rows.length * ROW_HEIGHT;
    this.content.addChild(this.buildPanel(rows, panelHeight));

    // Host status isn't on the `results` message itself (only ranking/
    // scores/slots), but the pre-race lobby state persists in Session
    // untouched through RaceScene, so it's still the source of truth for
    // "am I the host" here.
    const isHost = session.lobby?.players.find((p) => p.id === session.playerId)?.isHost ?? false;
    const controlsY = PANEL_TOP + panelHeight + CONTROLS_GAP_Y;

    if (isHost) {
      const rematchBtn = makeImageButton(this.buttonTexture!, 'Rematch', () => this.handleRematch(), {
        width: BUTTON_WIDTH,
        height: BUTTON_HEIGHT,
        textFill: COLORS.accentRed,
        tint: lighten(COLORS.accentRed, 0.5),
      });
      rematchBtn.x = LOGICAL_WIDTH / 2 - BUTTON_WIDTH - BUTTON_GAP / 2;
      rematchBtn.y = controlsY;
      this.content.addChild(rematchBtn);
      focusItems.push({ container: rematchBtn, activate: () => this.handleRematch() });
    } else {
      const waiting = makeLabel('Waiting for host...', { fontSize: 18, fill: COLORS.textLight });
      waiting.anchor.set(1, 0.5);
      waiting.x = LOGICAL_WIDTH / 2 - BUTTON_GAP / 2;
      waiting.y = controlsY + BUTTON_HEIGHT / 2;
      this.content.addChild(waiting);
    }

    const leaveBtn = makeImageButton(this.buttonTexture!, 'Leave', () => this.handleLeave(), {
      width: BUTTON_WIDTH,
      height: BUTTON_HEIGHT,
    });
    leaveBtn.x = LOGICAL_WIDTH / 2 + BUTTON_GAP / 2;
    leaveBtn.y = controlsY;
    this.content.addChild(leaveBtn);
    focusItems.push({ container: leaveBtn, activate: () => this.handleLeave() });

    this.focusRing?.setItems(focusItems);
  }

  private buildPanel(rows: ResultsRow[], height: number): Container {
    const panel = new Container();
    panel.x = PANEL_X;
    panel.y = PANEL_TOP;

    const bg = new Graphics().roundRect(0, 0, PANEL_WIDTH, height, 18).fill(COLORS.panel);
    bg.stroke({ width: 3, color: COLORS.outline });
    panel.addChild(bg);

    rows.forEach((row, i) => {
      const rowTop = PANEL_PAD_Y + i * ROW_HEIGHT;
      const rowY = rowTop + ROW_HEIGHT / 2;

      if (i > 0) {
        const divider = new Graphics()
          .moveTo(28, rowTop)
          .lineTo(PANEL_WIDTH - 28, rowTop)
          .stroke({ width: 1, color: COLORS.outline, alpha: 0.25 });
        panel.addChild(divider);
      }

      const rankText = makeLabel(`${row.rank}`, {
        fontSize: 22,
        fontWeight: '900',
        fill: row.isWinner ? COLORS.accentRed : COLORS.textDark,
      });
      rankText.anchor.set(0.5);
      rankText.x = 44;
      rankText.y = rowY;
      panel.addChild(rankText);

      const swatch = new Graphics().circle(96, rowY, 14).fill(row.colour);
      swatch.stroke({ width: 2, color: COLORS.outline });
      panel.addChild(swatch);

      const nameText = makeLabel(row.name, {
        fontSize: 20,
        fontWeight: row.isWinner ? '900' : '700',
        fill: COLORS.textDark,
      });
      nameText.anchor.set(0, 0.5);
      nameText.x = 128;
      nameText.y = rowY;
      panel.addChild(nameText);

      if (row.isWinner) {
        const badge = buildWinnerBadge();
        badge.x = 128 + nameText.width + 14;
        badge.y = rowY;
        panel.addChild(badge);
      }

      const pointsText = makeLabel(`${row.points} pts`, { fontSize: 20, fontWeight: '800', fill: COLORS.textDark });
      pointsText.anchor.set(1, 0.5);
      pointsText.x = PANEL_WIDTH - 32;
      pointsText.y = rowY;
      panel.addChild(pointsText);
    });

    return panel;
  }

  private handleRematch(): void {
    session.net.send({ t: 'rematch' });
  }

  private handleLeave(): void {
    session.net.send({ t: 'leave' });
    session.lobby = null;
    void this.scenes.go(new TitleScene(this.scenes));
  }
}

function buildWinnerBadge(): Container {
  const badge = new Container();
  const label = makeLabel('WINNER', { fontSize: 12, fontWeight: '900', fill: COLORS.textDark });
  const paddingX = 10;
  const height = 22;
  const width = label.width + paddingX * 2;
  const bg = new Graphics().roundRect(0, -height / 2, width, height, 11).fill(COLORS.accentYellow);
  label.anchor.set(0, 0.5);
  label.x = paddingX;
  badge.addChild(bg, label);
  return badge;
}
